import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { MessageDialogComponent } from './../../../shared-component/message-dialog/message-dialog.component';
import { Guid } from 'guid-typescript';
import { SetupsService } from '../../../pages/Setup/setup.service';
import { Router } from '@angular/router';
import { SessionUser } from './../../../auth/session.user';
import { AuthenticationService } from './../../../auth/authentication.service';
import { Component, OnInit, HostListener } from '@angular/core';
import { PerfectScrollbarConfigInterface } from '@harvest-dev/ng-perfect-scrollbar';
import { AppSettings } from '../../../app.settings';
import { Settings } from '../../../app.settings.model';
import { MenuService } from '../menu/menu.service';
import { AttachmentDialogComponent } from '../../../shared-component/attachment-dialog/attachment-dialog.component';
import { Attachment, AttachmentFilter, AttachmentType } from '../../../shared-component/attachment-dialog/attachment.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ChangePasswordComponent } from '../../../pages/Setup/change-password/change-password.component';

@Component({
  selector: 'app-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  providers: [MenuService],
  standalone: false
})
export class SidenavComponent implements OnInit {
  public psConfig: PerfectScrollbarConfigInterface = { wheelPropagation: true };
  public menuItems: Array<any>;
  public settings: Settings;
  public currentUser: SessionUser;
  private lastFixedSidenav: boolean | null = null;
  public currentUserService: any;
  public currentUserProfile: any;
  public currentUserRole: any;
  public currentUserRoleId: any;
  public currentUserLogo: any;
  private currentUserPersonPic: any;
  hideServiceSelect = false;
  isRoot = false;
  isSuperAdmin = false;
  isAdmin = false;
  isDeveloper = false;
  currentUserSubscription: Guid;
  returnUrl: string = null;
  roleDropdownOpen = false;
  serviceDropdownOpen = false;
  profileDropdownOpen = false;
  canChangeProfilePhoto = false;

  constructor(
    public appSettings: AppSettings,
    public menuService: MenuService,
    private authenticationService: AuthenticationService,
    public router: Router,
    public setupsService: SetupsService,
    public messageDialog: MatDialog,
    public dialog: MatDialog,
    private _snackBar: MatSnackBar,
    public translate: TranslateService
  ) {
    this.settings = this.appSettings.settings;
    this.currentUser = this.authenticationService.currentUserValue;

    if (this.currentUser) {
      this.isRoot = !!this.currentUser.isRoot;
      this.isSuperAdmin = !!this.currentUser.isSuperAdmin;
      this.isAdmin = !!this.currentUser.isAdmin;
      this.isDeveloper = !!this.currentUser.isDeveloper;
      this.currentUserService = localStorage.getItem('currentUserService');
      this.currentUserProfile = localStorage.getItem('currentProfileId');
      this.currentUserRole = localStorage.getItem('currentUserRole');
      this.currentUserRoleId = localStorage.getItem('currentUserRoleId');
      this.currentUserSubscription = localStorage.getItem('currentUserSubscription') as unknown as Guid;
      this.currentUserLogo = localStorage.getItem('currentUserLogo') || '';
    }

    this.returnUrl = localStorage.getItem('returnUrl');

    if (this.currentUser?.personPic != null) {
      this.currentUserPersonPic = this.currentUser.personPic.fileUrl;
      localStorage.setItem('currentUserPersonPic', this.currentUserPersonPic);
    } else {
      this.currentUserPersonPic = null;
    }

    const sessionUserStorage = localStorage.getItem('sessionUser');
    if (sessionUserStorage) {
      const sessionUserObj = JSON.parse(sessionUserStorage);
      if (sessionUserObj?.userKindId) {
        this.canChangeProfilePhoto = sessionUserObj.userKindId !== '12-04';
      }
    }

    this.updateServiceSelectVisibility();
  }

  ngOnInit() {
    this.settings.menuType = 'default';
    localStorage.setItem('menuType', 'default');

    if (this.currentUserPersonPic == null)
      this.getSubscriptionLogo();
    else {
      this.currentUserLogo = this.currentUserPersonPic;
      localStorage.setItem('currentUserLogo', this.currentUserLogo);
    }
  }

  ngDoCheck() {
    const fixed = !!this.settings.fixedSidenav;
    if (this.lastFixedSidenav === fixed) { return; }
    this.lastFixedSidenav = fixed;
    this.psConfig.wheelPropagation = !fixed;
  }

  // ─── Dropdowns ───────────────────────────────────────────────────────────────

  toggleServiceDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.serviceDropdownOpen = !this.serviceDropdownOpen;
    if (this.serviceDropdownOpen) {
      this.profileDropdownOpen = false;
      this.roleDropdownOpen = false;
    }
  }

  toggleProfileDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.profileDropdownOpen = !this.profileDropdownOpen;
    if (this.profileDropdownOpen) {
      this.serviceDropdownOpen = false;
      this.roleDropdownOpen = false;
    }
  }

  toggleRoleDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.roleDropdownOpen = !this.roleDropdownOpen;
    if (this.roleDropdownOpen) {
      this.serviceDropdownOpen = false;
      this.profileDropdownOpen = false;
    }
  }

  @HostListener('document:click')
  closeDropdowns(): void {
    this.roleDropdownOpen = false;
    this.serviceDropdownOpen = false;
    this.profileDropdownOpen = false;
  }

  // ─── Select handlers ─────────────────────────────────────────────────────────

  selectService(serviceId: any): void {
    this.currentUserService = serviceId;
    this.serviceDropdownOpen = false;

    // Reset profile to first available for the new service
    const profilesForService = (this.currentUser?.profiles || []).filter(p =>
      this.normalizeId(p.serviceId) === this.normalizeId(serviceId)
    );
    if (profilesForService.length > 0) {
      this.currentUserProfile = profilesForService[0].profileId;
      localStorage.setItem('currentProfileId', this.currentUserProfile);
      //localStorage.setItem('currentUserKindId', profilesForService[0].userKindId || '');

      // Auto-select first role for that profile
      const rolesForProfile = (this.currentUser?.roles || []).filter(r =>
        this.normalizeId(r.profileId) === this.normalizeId(this.currentUserProfile)
      );
      if (rolesForProfile.length > 0) {
        this.currentUserRole = rolesForProfile[0].name;
        this.currentUserRoleId = rolesForProfile[0].id;
        localStorage.setItem('currentUserRole', this.currentUserRole);
        localStorage.setItem('currentUserRoleId', this.currentUserRoleId);
      }
    }

    this.changeCurrentUserService();
  }

  selectProfile(profileId: any): void {
    this.currentUserProfile = profileId;
    this.profileDropdownOpen = false;
    this.changeCurrentUserProfile();
  }

  selectRole(roleName: string): void {
    this.currentUserRole = roleName;
    this.roleDropdownOpen = false;
    this.changeCurrentUserRole();
  }

  // ─── Change handlers ─────────────────────────────────────────────────────────

  public changeCurrentUserService(): void {
    localStorage.removeItem('parentStudents');
    localStorage.setItem('currentUserService', this.currentUserService);

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('questionAttachment')) {
        localStorage.removeItem(key);
      }
    }

    this.updateServiceSelectVisibility();
    setTimeout(() => { location.href = '/'; }, 500);
  }

  public changeCurrentUserProfile(): void {
    localStorage.setItem('currentProfileId', this.currentUserProfile);

    // Update userKindId for the selected profile
    const profile = (this.currentUser?.profiles || []).find(p =>
      this.normalizeId(p.profileId) === this.normalizeId(this.currentUserProfile)
    );
    // if (profile)
    //   localStorage.setItem('currentUserKindId', profile.userKindId || '');

    // Auto-select first role for the selected profile
    const rolesForProfile = (this.currentUser?.roles || []).filter(r =>
      this.normalizeId(r.profileId) === this.normalizeId(this.currentUserProfile)
    );
    if (rolesForProfile.length > 0) {
      this.currentUserRole = rolesForProfile[0].name;
      this.currentUserRoleId = rolesForProfile[0].id;
      localStorage.setItem('currentUserRole', this.currentUserRole);
      localStorage.setItem('currentUserRoleId', this.currentUserRoleId);
    }

    location.href = '/';
  }

  public changeCurrentUserRole(): void {
    localStorage.setItem('currentUserRole', this.currentUserRole);
    const role = (this.currentUser?.roles || []).find(r =>
      r.name === this.currentUserRole &&
      this.normalizeId(r.profileId) === this.normalizeId(this.currentUserProfile)
    );
    if (role) {
      this.currentUserRoleId = role.id;
      localStorage.setItem('currentUserRoleId', this.currentUserRoleId);
    }
    this.updateServiceSelectVisibility();
    location.href = '/';
  }

  // ─── Other ───────────────────────────────────────────────────────────────────

  public getSubscriptionLogo() {
    this.setupsService.getSubscriptionLogo(this.currentUserService).subscribe(result => {
      this.currentUserLogo = result.logo.fileUrl;
      localStorage.setItem('currentUserLogo', this.currentUserLogo);
    });
  }

  public closeSubMenus() {
    const menu = document.getElementById('vertical-menu');
    if (!menu?.children[0]) { return; }
    const container = menu.children[0];
    for (let i = 0; i < container.children.length; i++) {
      const child = container.children[i];
      const first = child?.children[0];
      const second = child?.children[1];
      if (first?.classList?.contains('expanded') && second) {
        first.classList.remove('expanded');
        second.classList.remove('show');
      }
    }
  }

  openProfilePhotoDialog() {
    let data = new AttachmentFilter();
    data.multiFiles = false;
    data.attachmentType = AttachmentType.UserProfile;
    data.preview = true;
    data.fileType = 'image';

    const dialogRef = this.dialog.open(AttachmentDialogComponent, {
      data: data,
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((attachmentList: Array<Attachment>) => {
      if (attachmentList?.length > 0) {
        const attachmentId = attachmentList[0].id;
        const profileId = localStorage.getItem('currentProfileId') as unknown as Guid;
        this.setupsService.updateProfilePhoto(profileId, attachmentId).subscribe(result => {
          if (result) {
            this._snackBar.open(this.translate.instant('Profile photo updated successfully'), null, { duration: 2000 });
            this.currentUserLogo = result;
            if (!this.currentUser.personPic)
              this.currentUser.personPic = new Attachment();
            this.currentUser.personPic.fileUrl = result;
            localStorage.setItem('sessionUser', JSON.stringify(this.currentUser));
            localStorage.setItem('currentUserPersonPic', this.currentUserLogo);
            localStorage.setItem('currentUserLogo', this.currentUserLogo);
          }
        });
      }
    });
  }

  openChangePasswordDialog(): void {
    const staffObj = { userName: this.currentUser?.userName };
    this.dialog.open(ChangePasswordComponent, {
      data: { staffObj },
      disableClose: true
    });
  }

  private backToUrlSystem() {
    this.authenticationService.logout();
  }

  private updateServiceSelectVisibility() {
    const selectedService = (this.currentUser?.services || []).find(s =>
      this.normalizeId(s?.service?.id) === this.normalizeId(this.currentUserService)
    );
    const serviceRoleId = selectedService?.service?.roleId;
    this.hideServiceSelect = !!serviceRoleId &&
      this.normalizeId(serviceRoleId) === this.normalizeId(this.currentUserRoleId);
  }

  private normalizeId(value: any): string {
    if (value === null || value === undefined) return '';
    return String(value).toLowerCase().trim();
  }

  // ─── Getters ─────────────────────────────────────────────────────────────────

  get currentServiceName(): string {
    const selected = (this.currentUser?.services || []).find(s =>
      this.normalizeId(s?.service?.id) === this.normalizeId(this.currentUserService)
    );
    return selected?.service?.name || '';
  }

  get currentProfileName(): string {
    const selected = (this.currentUser?.profiles || []).find(p =>
      this.normalizeId(p.profileId) === this.normalizeId(this.currentUserProfile)
    );
    return selected?.userKind || '';
  }

  get currentProfileOptions(): Array<any> {
    return (this.currentUser?.profiles || []).filter(p =>
      this.normalizeId(p.serviceId) === this.normalizeId(this.currentUserService)
    );
  }

  get currentRoleOptions(): Array<any> {
    return (this.currentUser?.roles || []).filter(r =>
      this.normalizeId(r.profileId) === this.normalizeId(this.currentUserProfile)
    );
  }



  trackByServiceId(index: number, item: any) { return item.service.id; }
  trackByProfileId(index: number, item: any) { return item.profileId; }
  trackByRoleName(index: number, item: any) { return item.name; }
}